// components/RulesNudge.jsx
// Shows a popup nudging the user to set their trading rules if none are set.
// Re-appears every NUDGE_INTERVAL_MS if still dismissed without setting rules.
// Mount once in App.jsx — it watches `page` and `rules` automatically.

import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

const NUDGE_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes — change freely
const NUDGE_PAGES       = new Set(['d', 'j', 'analytics', 'nb', 'ai', 'sim', 'chart']);

export default function RulesNudge({ rules = [], page, onGoToSettings }) {
  const [visible,   setVisible]   = useState(false);
  const [exiting,   setExiting]   = useState(false);
  const timerRef = useRef(null);

  const hasRules = Array.isArray(rules) && rules.filter(r => r?.trim()).length > 0;

  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => { setVisible(false); setExiting(false); }, 280);
  }, []);

  const goSettings = useCallback(() => {
    dismiss();
    onGoToSettings();
  }, [dismiss, onGoToSettings]);

  // Show nudge — only if no rules set and on a relevant page
  const tryShow = useCallback(() => {
    if (hasRules) return;
    if (!NUDGE_PAGES.has(page)) return;
    setExiting(false);
    setVisible(true);
  }, [hasRules, page]);

  useEffect(() => {
    // Clear any running timer when rules status or page changes
    clearInterval(timerRef.current);

    if (hasRules) {
      setVisible(false); // rules set — hide forever
      return;
    }

    // Show immediately when landing on a nudge-eligible page
    tryShow();

    // Re-show every NUDGE_INTERVAL_MS while rules are still missing
    timerRef.current = setInterval(tryShow, NUDGE_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [hasRules, page, tryShow]);

  if (!visible) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        // Anchor bottom-right above the bottom nav
        bottom: 96, right: 20,
        zIndex: 999998,
        width: 320,
        background: '#111318',
        border: '1px solid rgba(212,168,67,0.35)',
        borderRadius: 14,
        boxShadow: '0 20px 60px rgba(0,0,0,0.65), 0 0 0 1px rgba(212,168,67,0.08)',
        overflow: 'hidden',
        transition: 'opacity .28s ease, transform .28s cubic-bezier(.4,0,.2,1)',
        opacity:   exiting ? 0 : 1,
        transform: exiting ? 'translateY(12px) scale(.97)' : 'translateY(0) scale(1)',
        animation: 'nudgeIn .3s cubic-bezier(.4,0,.2,1) both',
      }}
    >
      {/* Gold top accent */}
      <div style={{ height: 3, background: 'linear-gradient(90deg,#c4a05a,#e0c080,#c4a05a)' }} />

      <div style={{ padding: '16px 16px 14px' }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            {/* Icon */}
            <span style={{
              width: 34, height: 34, borderRadius: 9, flexShrink: 0,
              background: 'rgba(212,168,67,0.12)', border: '1px solid rgba(212,168,67,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 17,
            }}>
              📋
            </span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#E8E4DC', lineHeight: 1.3 }}>
                Set Your Trading Rules
              </div>
              <div style={{ fontSize: 10.5, color: '#D4A843', fontWeight: 600, marginTop: 1 }}>
                Action required
              </div>
            </div>
          </div>

          {/* Close button */}
          <button
            onClick={dismiss}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'rgba(232,228,220,0.25)', fontSize: 18, lineHeight: 1,
              padding: '2px 4px', borderRadius: 4, flexShrink: 0,
              transition: 'color .14s',
            }}
            onMouseEnter={e => e.currentTarget.style.color = 'rgba(232,228,220,0.7)'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(232,228,220,0.25)'}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <p style={{
          fontSize: 12.5, lineHeight: 1.55,
          color: 'rgba(232,228,220,0.6)',
          margin: '0 0 14px',
          fontFamily: "'Inter', sans-serif",
        }}>
          You haven't set any trading rules yet. Rules appear as checklists on every trade so you can track your discipline.
        </p>

        {/* CTA buttons */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={goSettings}
            style={{
              flex: 1,
              background: 'linear-gradient(135deg, rgba(212,168,67,0.22), rgba(212,168,67,0.10))',
              border: '1px solid rgba(212,168,67,0.4)',
              borderRadius: 8, padding: '9px 12px',
              color: '#D4A843', fontSize: 12, fontWeight: 700,
              cursor: 'pointer', fontFamily: "'Inter', sans-serif",
              letterSpacing: '.03em',
              transition: 'all .15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(212,168,67,0.25)'; e.currentTarget.style.borderColor = 'rgba(212,168,67,0.6)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(212,168,67,0.22), rgba(212,168,67,0.10))'; e.currentTarget.style.borderColor = 'rgba(212,168,67,0.4)'; }}
          >
            Go to Settings →
          </button>
          <button
            onClick={dismiss}
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 8, padding: '9px 12px',
              color: 'rgba(232,228,220,0.35)', fontSize: 12, fontWeight: 500,
              cursor: 'pointer', fontFamily: "'Inter', sans-serif",
              transition: 'all .15s',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = 'rgba(232,228,220,0.6)'; e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(232,228,220,0.35)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
          >
            Later
          </button>
        </div>

        {/* Re-appear hint */}
        <div style={{ marginTop: 10, fontSize: 10, color: 'rgba(232,228,220,0.2)', textAlign: 'center', fontFamily: 'monospace' }}>
          This reminder will reappear in 2 minutes
        </div>
      </div>

      <style>{`
        @keyframes nudgeIn {
          from { opacity: 0; transform: translateY(16px) scale(.96); }
          to   { opacity: 1; transform: translateY(0)    scale(1);   }
        }
      `}</style>
    </div>,
    document.body
  );
}
